import useAuthSuccessCallback from './useAuthSuccessCallback'
import { STATUS, UNAUTHORIZED_TYPES } from 'utils/constants'
import { newmailcode, newsmscode, verify } from 'rest/urls'
import { selectRestPopupBody } from 'rest/restSlice'
import useTranslation from 'hooks/useTranslation'
import ResendCode from 'components/ResendCode'
import Submit from 'components/inputs/Submit'
import PinForm from 'components/PinForm'
import { useReduxSelector } from 'store'
import { POST } from 'rest/request'
import { useState } from 'react'

const CODE_LENGTH = 6
type StatusNames = 'getting' | 'sending'
type Status = Record<StatusNames, keyof typeof STATUS>

const VerificationPopup = () => {

  const popupBody = useReduxSelector(selectRestPopupBody)
  const { type, code_sent, recipient: r, additional_recipient } = popupBody as rest.UnauthorizedResponse

  const [recipient, setRecipient] = useState(r)
  const [warning, setWarning] = useState(false)
  const [token, setToken] = useState('')
  const [status, setStatus] = useState<Status>({
    getting: STATUS.idle,
    sending: STATUS.idle
  })

  const { t } = useTranslation('auth')
  const successCallback = useAuthSuccessCallback()
  const getSubmitText = (l: number) => l === CODE_LENGTH
    ? t('authorize')
    : `${CODE_LENGTH - l} ${t(l === CODE_LENGTH - 1 ? 'characterIsMissing' : 'charactersAreMissing')}`

  const updateStatus = (status: keyof typeof STATUS, name: StatusNames = 'sending') => {
    setStatus(s => ({ ...s, [name]: status }))
  }

  const submit = async () => {
    updateStatus(STATUS.pending)
    try {
      const body = {
        token,
        [type]: r
      }
      const response = await POST(verify(type), { body, baseRequest: true })
      if (response?.verified) {
        successCallback({ success: true })
      } else {
        throw new Error('Wrong code')
      }

    } catch (err) {
      updateStatus(STATUS.failed)
      setWarning(true)
    }
  }

  const getCode = async (additional = false) => {
    if (status.getting === STATUS.pending) return
    updateStatus(STATUS.pending, 'getting')

    try {
      const body = { [type]: r, ...(additional && { additional: '1' }) }
      const response = await POST(type === UNAUTHORIZED_TYPES.mail ? newmailcode : newsmscode, { body })
      if (response) {
        updateStatus(STATUS.succeed, 'getting')
        setRecipient(response?.recipient || (additional ? additional_recipient : r))
      } else throw new Error('Wrong response')

    } catch (error) {
      updateStatus(STATUS.failed, 'getting')
      console.error('Code is not sent!')
      return false
    }
  }

  return (
    <div className='popup__verification'>
      <div className='popup__title'>
        <i className='icon icon--logo s24' />
        <div>{t(
          type === UNAUTHORIZED_TYPES.device
            ? 'deviceAuthorization'
            : type === UNAUTHORIZED_TYPES.mail
              ? 'emailAuthorization'
              : 'smsAuthorization'
        )}</div>
      </div>

      {(code_sent || status.getting === STATUS.succeed) && <>
        <div className='verification__text'>
          <div>{t('rewriteTheVerificationCode')}</div>
          <div>{t('sentTo')}</div>
        </div>

        <h2>{recipient}</h2>
      </>}

      <PinForm
        isCode
        autoFocus
        value={token}
        warning={warning}
        setValue={setToken}
        submit={submit}
        setWarning={w => setWarning(w)}
        autoLoadCodeFromSMS={status.sending === STATUS.idle && type !== UNAUTHORIZED_TYPES.device}
      />

      <ResendCode
        className='verification__phone'
        text={t((status.getting === STATUS.idle && !code_sent) ? 'sendCode' : 'pleaseResend')}
        recipient={r}
        additionalRecipient={(code_sent || status.getting !== STATUS.idle) ? additional_recipient : undefined}
        send={getCode}
      />

      <Submit
        text={getSubmitText(token.length)}
        block={token.length !== CODE_LENGTH || status.sending === STATUS.pending}
        action={submit}
      />
    </div>
  )
}

export default VerificationPopup